// @ts-nocheck

import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Search, UserSearch, UserPlus } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useScoutAssignments } from '@/hooks/useScoutAssignments';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import AthleteActionDrawer from '@/components/scout/AthleteActionDrawer';
import ScoutConnectButton from '@/components/scout/ScoutConnectButton';

interface AssignedAthlete {
  id: string;
  username: string;
  email: string;
  sport_id: string;
  grad_year?: number;
  avatar_url?: string;
  assigned_at: string;
}

interface AthleteProfile {
  id: string;
  username: string;
  email: string;
  sport_id: string;
  grad_year?: number;
  avatar_url?: string;
  name?: string;
  city?: string;
  state?: string;
}

const ScoutAthletes = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [newAthleteSearch, setNewAthleteSearch] = useState('');
  const [searchResults, setSearchResults] = useState<AthleteProfile[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedAthlete, setSelectedAthlete] = useState<AssignedAthlete | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { assignedAthletes, isLoading } = useScoutAssignments();
  const { profile } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const filteredAthletes = assignedAthletes.filter(athlete => 
    athlete.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    athlete.sport_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    athlete.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const searchNewAthletes = async () => {
    if (!newAthleteSearch.trim() || !profile?.id) return;

    setIsSearching(true);
    try {
      // First get connected athlete IDs
      const { data: connectedAthletes } = await supabase
        .from('scout_athlete_relationships')
        .select('athlete_id')
        .eq('scout_id', profile.id)
        .eq('status', 'active');

      const connectedIds = connectedAthletes?.map(rel => rel.athlete_id) || [];

      // Then search for athletes excluding connected ones
      let query = supabase
        .from('profiles')
        .select('id, username, email, sport_id, grad_year, avatar_url, name, city, state')
        .eq('role', 'athlete')
        .eq('status', 'active')
        .or(`username.ilike.%${newAthleteSearch}%,email.ilike.%${newAthleteSearch}%,name.ilike.%${newAthleteSearch}%`)
        .limit(5);

      if (connectedIds.length > 0) {
        query = query.not('id', 'in', `(${connectedIds.map(id => `"${id}"`).join(',')})`);
      }

      const { data, error } = await query;

      if (error) throw error;
      setSearchResults(data || []);
    } catch (error: any) {
      console.error('Error searching athletes:', error);
      toast({
        title: "Search Error",
        description: "Failed to search athletes. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSearching(false);
    }
  };

  // Auto-search when typing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (newAthleteSearch.trim()) {
        searchNewAthletes();
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [newAthleteSearch]);

  const handleViewProfile = (athleteId: string) => {
    const targetPath = `/athlete-dashboard?profile_id=${athleteId}&asScout=1`;
    navigate(targetPath);
  };

  const handleCardClick = (athlete: AssignedAthlete) => {
    setSelectedAthlete(athlete);
    setIsDrawerOpen(true);
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedAthlete(null);
  };

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">My Athletes</h1>
          <p className="text-muted-foreground">Loading your connected athletes...</p>
        </div>
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-muted rounded-full"></div>
                  <div className="space-y-2 flex-1">
                    <div className="h-4 bg-muted rounded w-1/4"></div>
                    <div className="h-3 bg-muted rounded w-1/2"></div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6">
      <div className="space-y-2">
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight">My Athletes</h1>
        <p className="text-sm md:text-base text-muted-foreground">
          Athletes connected to you for recruitment tracking
        </p>
      </div>

      {/* Search for new athletes */}
      <div className="space-y-4">
        <div className="flex items-center">
          <div className="relative flex-1">
            <UserPlus className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Search for new athletes to connect with..."
              value={newAthleteSearch}
              onChange={(e) => setNewAthleteSearch(e.target.value)}
              className="pl-10 min-h-[44px]"
            />
          </div>
        </div>

        {/* Search results for new athletes */}
        {isSearching && (
          <div className="animate-pulse space-y-2">
            {[1, 2].map((i) => (
              <Card key={i}>
                <CardContent className="p-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-muted rounded-full"></div>
                    <div className="space-y-2 flex-1">
                      <div className="h-3 bg-muted rounded w-1/4"></div>
                      <div className="h-2 bg-muted rounded w-1/2"></div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {searchResults.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-muted-foreground">Available Athletes</h3>
            {searchResults.map((athlete) => (
              <Card key={athlete.id} className="hover:shadow-sm transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={athlete.avatar_url} alt={athlete.username} />
                        <AvatarFallback className="bg-blue-100 text-blue-600">
                          {athlete.username.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 truncate text-sm">
                          {athlete.name || athlete.username}
                        </h4>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-muted-foreground">Sport ID: {athlete.sport_id}</span>
                          {athlete.grad_year && (
                            <>
                              <span className="text-xs text-muted-foreground">•</span>
                              <span className="text-xs text-muted-foreground">
                                Class of {athlete.grad_year}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <ScoutConnectButton 
                        athleteId={athlete.id}
                        variant="default"
                        size="sm"
                        className="text-xs px-3 py-1"
                      />
                      <Button
                        onClick={() => handleViewProfile(athlete.id)}
                        variant="outline"
                        size="sm"
                        className="text-xs px-3 py-1"
                      >
                        View Profile
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Search connected athletes - Mobile responsive */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <h2 className="text-lg md:text-xl font-semibold">Connected Athletes</h2>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Search connected athletes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 min-h-[44px]"
            />
          </div>
        </div>
      </div>

      {/* Athletes Grid - Mobile responsive cards */}
      <div className="grid gap-3 md:gap-4">
        {filteredAthletes.map((athlete) => (
          <Card 
            key={athlete.id} 
            className="cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => handleCardClick(athlete)}
          >
            <CardContent className="p-4 md:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center space-x-3">
                  <Avatar className="h-10 w-10 md:h-12 md:w-12 flex-shrink-0">
                    <AvatarImage src={athlete.avatar_url} alt={athlete.username} />
                    <AvatarFallback className="bg-blue-100 text-blue-600 text-sm">
                      {athlete.username.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-900 truncate text-sm md:text-base">{athlete.username}</h4>
                    <p className="text-xs md:text-sm text-muted-foreground truncate">{athlete.email}</p>
                    <div className="flex flex-wrap items-center gap-1 mt-1">
                      <span className="text-xs text-muted-foreground">Sport ID: {athlete.sport_id}</span>
                      {athlete.grad_year && (
                        <>
                          <span className="text-xs text-muted-foreground">•</span>
                          <span className="text-xs text-muted-foreground">
                            Class of {athlete.grad_year}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2 self-start sm:self-center">
                  <Badge variant="default" className="text-xs">
                    Connected
                  </Badge>
                  <div onClick={(e) => e.stopPropagation()}>
                    <ScoutConnectButton 
                      athleteId={athlete.id}
                      variant="outline"
                      size="sm"
                      className="text-xs px-2 md:px-3 py-1 min-h-[32px]"
                    />
                  </div>
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewProfile(athlete.id);
                    }}
                    size="sm"
                    className="bg-green-600 hover:bg-green-700 text-white transition-colors text-xs px-2 md:px-3 py-1 min-h-[32px]"
                  >
                    <span className="hidden sm:inline">View Profile</span>
                    <span className="sm:hidden">View</span>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredAthletes.length === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <UserSearch className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No athletes found</h3>
            <p className="text-muted-foreground">
              {searchTerm ? 'Try adjusting your search terms' : 'No athletes are connected to you yet'}
            </p>
          </CardContent>
        </Card>
      )}

      <AthleteActionDrawer
        athlete={selectedAthlete}
        isOpen={isDrawerOpen}
        onClose={closeDrawer}
      />
    </div>
  );
};

export default ScoutAthletes;